/**
 * @module line/lineui
 */

import { Plugin } from 'ckeditor5/src/core';
import { addListToDropdown, createDropdown, Model } from 'ckeditor5/src/ui';
import { Collection } from 'ckeditor5/src/utils';
import { normalizeOptions } from './utils';

/**
 * The abstract line UI plugin.
 *
 * @extends Plugin
 */
export default class LineUI extends Plugin {

	/**
	 * Defines the values to be used
	 *
	 * @param {Editor} editor
	 * @param {String} attributeKey
	 * @param {String} defaultValue
	 * @param {String} defaultTitle
	 * @param {String} tooltip
	 */
	constructor( editor, attributekey, defaultValue, defaultTitle, tooltip ) {
		super( editor );
		/**
		 * @private
		 * @type {String}
		 */
		this.attributekey = attributekey;
		/**
		 * @private
		 * @type {String}
		 */
		this.defaultValue = defaultValue;
		/**
		 * @private
		 * @type {String}
		 */
		this.defaultTitle = defaultTitle;
		/**
		 * @private
		 * @type {String}
		 */
		this.tooltip = tooltip;
	}

	/**
	 * @inheritDoc
	 */
	init() {
		const editor = this.editor;
		const t = editor.t;
		const options = normalizeOptions( editor.config.get( `${ this.attributekey }.options` ), this.defaultValue );
		const defaultTitle = t( this.defaultTitle );
		const dropdownTooltip = t( this.tooltip );
		const command = editor.commands.get( this.attributekey );

		editor.ui.componentFactory.add( this.attributekey, locale => {
			const titles = {};
			const itemDefinitions = new Collection();

			for ( const option of options ) {
				const def = {
					type: 'button',
					model: new Model( {
						label: option.title,
						commandName: this.attributekey,
						commandValue: option.value,
						withText: true
					} )
				};
				def.model.bind( 'isOn' ).to( command, 'value', value => value === option.value );

				itemDefinitions.add( def );
				titles[ option.value ] = option.title;
			}

			const dropdownView = createDropdown( locale );
			addListToDropdown( dropdownView, itemDefinitions );

			dropdownView.buttonView.set( {
				label: defaultTitle,
				isOn: false,
				withText: true,
				tooltip: dropdownTooltip
			} );

			dropdownView.extendTemplate( {
				attributes: {
					class: [
						`ck-${ this.attributekey }-dropdown`
					]
				}
			} );

			dropdownView.bind( 'isEnabled' ).to( command );

			this.listenTo( dropdownView, 'execute', evt => {
				editor.execute( evt.source.commandName, evt.source.commandValue ? {
					value: evt.source.commandValue
				} : undefined );
				editor.editing.view.focus();
			} );

			return dropdownView;
		} );
	}
}
